import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import CourseStudentsModal from '../../Components/CourseStudentsModal';
import CourseFormModal from '../../Components/CourseFormModal';
import Toast from '../../Components/Toast';
import EnrollmentsView from './EnrollmentsView';
import {
  getCached,
  setCached,
  invalidate,
} from '../../utils/apiCache';
import './CourseAdmin.css';

const CACHE_KEYS = {
  COURSES: 'courseAdmin:courses',
  USERS: 'courseAdmin:users',
};
const CACHE_TTL = {
  COURSES: 60_000,
  USERS: 120_000,
};

const SERVER_URL = 'https://api.avtechbmt.cloud';
const COURSE_API_URL = `${SERVER_URL}/api/v1`;

const FALLBACK_IMG =
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60';

/* --------------------------------------------------------------------------
   EMPTY FORM
   -------------------------------------------------------------------------- */

const EMPTY_FORM = {
  code: '',
  title: '',
  category: 'programming',
  duration: '',
  level: 'Beginner',
  projects: 0,
  internship: '',
  description: '',
  detailedDescription: '',
  features: [''],
  syllabus: [{ module: '', content: '' }],
  imageUrl: '',
  price: 0,
  isActive: true,
  order: 0,
};

/* ==========================================================================
   MAIN COMPONENT
   ========================================================================== */

export default function CourseAdmin() {
  const { token } = useAuth();

  const [courses, setCourses] = useState(
    () => getCached(CACHE_KEYS.COURSES)?.data || []
  );
  const [usersWithEnrollments, setUsersWithEnrollments] = useState(
    () => getCached(CACHE_KEYS.USERS)?.data || []
  );

  const [loading, setLoading] = useState(() => !getCached(CACHE_KEYS.COURSES));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [currentSubTab, setCurrentSubTab] = useState('courses');

  // Toolbar
  const [courseSearch, setCourseSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order');

  // Selection (bulk)
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Kebab
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const menuRef = useRef(null);

  // Form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Students modal
  const [selectedCourseStudents, setSelectedCourseStudents] = useState(null);
  const [studentsModalLoading, setStudentsModalLoading] = useState(false);

  /* ============================================================
     HELPERS
     ============================================================ */

  const getImageUrl = useCallback((path) => {
    if (!path) return FALLBACK_IMG;
    if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
    const clean = path.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${SERVER_URL}/${clean}`;
  }, []);

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token')}`,
    }),
    [token]
  );

  /* ============================================================
     FETCHERS
     ============================================================ */

  const fetchCourses = useCallback(
    async ({ force = false } = {}) => {
      if (!force) {
        const cached = getCached(CACHE_KEYS.COURSES);
        if (cached) {
          setCourses(cached.data);
          setLoading(false);
          return;
        }
      }
      setLoading((prev) => (courses.length === 0 ? true : prev));
      try {
        const res = await fetch(`${COURSE_API_URL}/admin/courses`, {
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setCourses(data.data || []);
          setCached(CACHE_KEYS.COURSES, data.data || [], CACHE_TTL.COURSES);
        } else {
          setError(data.message || 'Failed to fetch courses.');
        }
      } catch {
        setError('Error connecting to backend server.');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAuthHeaders, courses.length]
  );

  const fetchUsersWithEnrollments = useCallback(
    async ({ force = false } = {}) => {
      if (!force) {
        const cached = getCached(CACHE_KEYS.USERS);
        if (cached) {
          setUsersWithEnrollments(cached.data);
          return;
        }
      }
      try {
        const res = await fetch(`${COURSE_API_URL}/admin/users`, {
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setUsersWithEnrollments(data.data || []);
          setCached(CACHE_KEYS.USERS, data.data || [], CACHE_TTL.USERS);
        }
      } catch (err) {
        console.error('Error fetching user enrollments:', err);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (currentSubTab === 'enrollments') fetchUsersWithEnrollments();
  }, [currentSubTab, fetchUsersWithEnrollments]);

  /* ============================================================
     KEBAB OUTSIDE CLICK
     ============================================================ */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId === null) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpenMenuId(null);
      setConfirmDeleteId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  /* Auto-dismiss */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  /* ============================================================
     DERIVED
     ============================================================ */

  const visibleCourses = useMemo(() => {
    let list = [...courses];
    if (courseSearch.trim()) {
      const q = courseSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.category?.toLowerCase().includes(q) ||
          c.code?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'active') list = list.filter((c) => c.isActive);
    if (statusFilter === 'inactive') list = list.filter((c) => !c.isActive);

    switch (sortBy) {
      case 'price-asc':
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'enrolled':
        list.sort(
          (a, b) =>
            (b.enrolledStudents?.length || 0) -
            (a.enrolledStudents?.length || 0)
        );
        break;
      default:
        list.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return list;
  }, [courses, courseSearch, statusFilter, sortBy]);

  const kpis = useMemo(
    () => ({
      totalCourses: courses.length,
      activeCourses: courses.filter((c) => c.isActive).length,
      totalStudents: usersWithEnrollments.length,
      totalEnrollments: courses.reduce(
        (sum, c) => sum + (c.enrolledStudents?.length || 0),
        0
      ),
    }),
    [courses, usersWithEnrollments]
  );

  /* ============================================================
     SELECTION
     ============================================================ */

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const allVisibleSelected =
    visibleCourses.length > 0 &&
    visibleCourses.every((c) => selectedIds.has(c._id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) clearSelection();
    else setSelectedIds(new Set(visibleCourses.map((c) => c._id)));
  };

  /* ============================================================
     BULK ACTIONS
     ============================================================ */

  const handleBulkToggleStatus = async (makeActive) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setError('');
    setSuccess('');
    try {
      await Promise.all(
        ids.map((id) =>
          fetch(`${COURSE_API_URL}/admin/courses/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ isActive: makeActive }),
          })
        )
      );
      setSuccess(
        `✅ ${ids.length} course${ids.length > 1 ? 's' : ''} ${makeActive ? 'activated' : 'hidden'
        }.`
      );
      invalidate(CACHE_KEYS.COURSES);
      fetchCourses({ force: true });
      clearSelection();
    } catch {
      setError('Bulk update failed.');
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Delete ${ids.length} course${ids.length > 1 ? 's' : ''}? This cannot be undone.`
      )
    )
      return;
    setError('');
    setSuccess('');
    try {
      await Promise.all(
        ids.map((id) =>
          fetch(`${COURSE_API_URL}/admin/courses/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          })
        )
      );
      setSuccess(`🗑️ Deleted ${ids.length} course${ids.length > 1 ? 's' : ''}.`);
      invalidate(CACHE_KEYS.COURSES);
      invalidate(CACHE_KEYS.USERS);
      fetchCourses({ force: true });
      fetchUsersWithEnrollments({ force: true });
      clearSelection();
    } catch {
      setError('Bulk delete failed.');
    }
  };

  /* ============================================================
     FORM MODAL HANDLERS
     ============================================================ */

  const openCreateModal = () => {
    setEditingCourse(null);
    setFormData({
      ...EMPTY_FORM,
      features: [''],
      syllabus: [{ module: '', content: '' }],
    });
    setShowFormModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (course) => {
    setOpenMenuId(null);
    setConfirmDeleteId(null);
    setEditingCourse(course);
    setFormData({
      code: course.code || '',
      title: course.title || '',
      category: course.category || 'programming',
      duration: course.duration || '',
      level: course.level || 'Beginner',
      projects: course.projects ?? 0,
      internship: course.internship || '',
      description: course.description || '',
      detailedDescription: course.detailedDescription || '',
      features:
        Array.isArray(course.features) && course.features.length > 0
          ? [...course.features]
          : [''],
      syllabus:
        Array.isArray(course.syllabus) && course.syllabus.length > 0
          ? course.syllabus.map((s) => ({
            module: s.module || '',
            content: s.content || '',
          }))
          : [{ module: '', content: '' }],
      imageUrl: course.imageUrl || '',
      price: course.price ?? 0,
      isActive: course.isActive ?? true,
      order: course.order ?? 0,
    });
    setShowFormModal(true);
    setError('');
    setSuccess('');
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
    setEditingCourse(null);
    setFormData(EMPTY_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFeatureChange = (index, value) => {
    setFormData((prev) => {
      const features = [...prev.features];
      features[index] = value;
      return { ...prev, features };
    });
  };

  const addFeature = () => {
    setFormData((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleNestedChange = (field, index, key, value) => {
    setFormData((prev) => {
      const list = [...prev[field]];
      list[index] = { ...list[index], [key]: value };
      return { ...prev, [field]: list };
    });
  };

  const addSyllabusModule = () => {
    setFormData((prev) => ({
      ...prev,
      syllabus: [...prev.syllabus, { module: '', content: '' }],
    }));
  };

  const removeSyllabusModule = (index) => {
    setFormData((prev) => ({
      ...prev,
      syllabus: prev.syllabus.filter((_, i) => i !== index),
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');

    const isEdit = Boolean(editingCourse);
    const url = isEdit
      ? `${COURSE_API_URL}/admin/courses/${editingCourse._id}`
      : `${COURSE_API_URL}/admin/courses`;
    const method = isEdit ? 'PUT' : 'POST';

    const cleanedFeatures = formData.features
      .map((f) => (typeof f === 'string' ? f.trim() : ''))
      .filter(Boolean);

    const cleanedSyllabus = formData.syllabus
      .filter((s) => s.module?.trim() || s.content?.trim())
      .map((s) => ({
        module: s.module?.trim() || '',
        content: s.content?.trim() || '',
      }));

    const payload = {
      code: formData.code.trim(),
      title: formData.title.trim(),
      category: formData.category,
      duration: formData.duration,
      level: formData.level,
      projects: Number(formData.projects) || 0,
      internship: formData.internship,
      description: formData.description,
      detailedDescription: formData.detailedDescription,
      features: cleanedFeatures,
      syllabus: cleanedSyllabus,
      imageUrl: formData.imageUrl,
      price: Number(formData.price) || 0,
      isActive: formData.isActive,
      order: Number(formData.order) || 0,
    };

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowFormModal(false);
        setEditingCourse(null);
        setFormData(EMPTY_FORM);
        setSuccess(
          isEdit
            ? '✅ Course updated successfully!'
            : '🎉 Course added successfully!'
        );
        invalidate(CACHE_KEYS.COURSES);
        invalidate(CACHE_KEYS.USERS);
        fetchCourses({ force: true });
      } else {
        setError(data.message || 'Failed to save course.');
      }
    } catch {
      setError('Network error saving course.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     SINGLE-CARD ACTIONS
     ============================================================ */

  const toggleCourseStatus = async (course) => {
    setOpenMenuId(null);
    setConfirmDeleteId(null);
    setError('');
    setSuccess('');
    const prev = courses;
    setCourses((p) =>
      p.map((c) =>
        c._id === course._id ? { ...c, isActive: !c.isActive } : c
      )
    );
    try {
      const res = await fetch(
        `${COURSE_API_URL}/admin/courses/${course._id}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ isActive: !course.isActive }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(
          `Course "${course.title}" ${!course.isActive ? 'activated' : 'hidden'
          }.`
        );
        invalidate(CACHE_KEYS.COURSES);
        fetchCourses({ force: true });
      } else {
        setCourses(prev);
        setError(data.message || 'Failed to toggle status.');
      }
    } catch {
      setCourses(prev);
      setError('Failed to update status.');
    }
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    setConfirmDeleteId(null);
    setOpenMenuId(null);
    setError('');
    setSuccess('');
    const prev = courses;
    setCourses((p) => p.filter((c) => c._id !== courseId));
    try {
      const res = await fetch(`${COURSE_API_URL}/admin/courses/${courseId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`🗑️ Course "${courseTitle}" deleted.`);
        invalidate(CACHE_KEYS.COURSES);
        invalidate(CACHE_KEYS.USERS);
        fetchCourses({ force: true });
        fetchUsersWithEnrollments({ force: true });
      } else {
        setCourses(prev);
        setError(data.message || 'Failed to delete.');
      }
    } catch {
      setCourses(prev);
      setError('Network error deleting course.');
    }
  };

  const handleViewCourseStudents = async (courseId) => {
    setStudentsModalLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${COURSE_API_URL}/admin/courses/${courseId}/students`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (res.ok && data.success) setSelectedCourseStudents(data);
      else setError(data.message || 'Failed to fetch students.');
    } catch {
      setError('Error connecting to server.');
    } finally {
      setStudentsModalLoading(false);
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div className="CourseAdmin-container">
      <Toast type="success" message={success} onClose={() => setSuccess('')} />

      {/* Header */}
      <div className="CourseAdmin-header">
        <div>
          <span className="CourseAdmin-field-label">
            06 / Curriculum &amp; Academy
          </span>
          <h2>Course Management Center</h2>
        </div>
        <button className="CourseAdmin-cta-primary" onClick={openCreateModal}>
          ＋ Add New Course
        </button>
      </div>

      {error && (
        <div className="CourseAdmin-alert-box CourseAdmin-alert-error">
          ⚠️ {error}
        </div>
      )}

      {/* KPI */}
      <div className="CourseAdmin-kpi-strip">
        <KPI label="Total Courses" value={kpis.totalCourses} />
        <KPI label="Active" value={kpis.activeCourses} tone="green" />
        <KPI label="Total Students" value={kpis.totalStudents} />
        <KPI label="Total Enrollments" value={kpis.totalEnrollments} tone="blue" />
      </div>

      {/* Tabs */}
      <div className="CourseAdmin-tab-bar">
        <button
          className={`CourseAdmin-tab-btn ${currentSubTab === 'courses' ? 'active' : ''
            }`}
          onClick={() => setCurrentSubTab('courses')}
        >
          📚 All Courses ({courses.length})
        </button>
        <button
          className={`CourseAdmin-tab-btn ${currentSubTab === 'enrollments' ? 'active' : ''
            }`}
          onClick={() => setCurrentSubTab('enrollments')}
        >
          🎓 User Enrollments ({usersWithEnrollments.length})
        </button>
      </div>

      {/* COURSES TAB */}
      {currentSubTab === 'courses' && (
        <>
          <div className="CourseAdmin-toolbar">
            <input
              className="CourseAdmin-form-input CourseAdmin-toolbar-search"
              placeholder="🔍 Search by title, code, or category..."
              value={courseSearch}
              onChange={(e) => setCourseSearch(e.target.value)}
            />
            <select
              className="CourseAdmin-form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Hidden Only</option>
            </select>
            <select
              className="CourseAdmin-form-input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="order">Sort: Order Index</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
              <option value="enrolled">Most Enrolled</option>
            </select>
            <button
              className="CourseAdmin-cta-primary"
              style={{
                background:
                  selectedIds.size > 0
                    ? 'var(--accent, #2563eb)'
                    : 'transparent',
                color:
                  selectedIds.size > 0 ? '#fff' : 'var(--text, #0f172a)',
                borderColor: 'var(--border, #e2e8f0)',
              }}
              onClick={toggleSelectAll}
              disabled={visibleCourses.length === 0}
            >
              {allVisibleSelected ? '☑ Deselect All' : '☐ Select All'}
            </button>
          </div>

          {/* Bulk bar */}
          {selectedIds.size > 0 && (
            <div className="CourseAdmin-bulk-bar">
              <span className="CourseAdmin-bulk-info">
                {selectedIds.size} course{selectedIds.size > 1 ? 's' : ''}{' '}
                selected
              </span>
              <div className="CourseAdmin-bulk-actions">
                <button
                  className="CourseAdmin-bulk-btn"
                  onClick={() => handleBulkToggleStatus(true)}
                >
                  👁️ Show
                </button>
                <button
                  className="CourseAdmin-bulk-btn"
                  onClick={() => handleBulkToggleStatus(false)}
                >
                  🚫 Hide
                </button>
                <button
                  className="CourseAdmin-bulk-btn danger"
                  onClick={handleBulkDelete}
                >
                  🗑️ Delete
                </button>
                <button
                  className="CourseAdmin-bulk-btn"
                  onClick={clearSelection}
                >
                  ✕ Clear
                </button>
              </div>
            </div>
          )}

          {/* Grid / Empty / Skeleton */}
          {loading && courses.length === 0 ? (
            <div className="CourseAdmin-skeleton-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="CourseAdmin-skeleton-card" />
              ))}
            </div>
          ) : visibleCourses.length === 0 ? (
            <div className="CourseAdmin-empty-state">
              <span className="icon">📚</span>
              <span>
                {courses.length === 0
                  ? 'No courses added yet. Click "Add New Course" above.'
                  : 'No courses match your current filters.'}
              </span>
            </div>
          ) : (
            <div className="CourseAdmin-grid">
              {visibleCourses.map((course) => {
                const isSelected = selectedIds.has(course._id);
                const isMenuOpen = openMenuId === course._id;
                return (
                  <div
                    key={course._id}
                    className={`CourseAdmin-card ${!course.isActive ? 'is-inactive' : ''
                      } ${isSelected ? 'is-selected' : ''}`}
                  >
                    <button
                      type="button"
                      className={`CourseAdmin-card-select ${isSelected ? 'checked' : ''
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(course._id);
                      }}
                      aria-label={isSelected ? 'Deselect' : 'Select'}
                    >
                      ✓
                    </button>

                    <div className="CourseAdmin-card-image-wrapper">
                      <img
                        src={getImageUrl(course.imageUrl)}
                        alt={course.title}
                        className="CourseAdmin-card-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = FALLBACK_IMG;
                        }}
                      />
                      <span
                        className={`CourseAdmin-badge-status ${course.isActive
                            ? 'CourseAdmin-status-active'
                            : 'CourseAdmin-status-inactive'
                          }`}
                      >
                        {course.isActive ? '● Active' : '○ Hidden'}
                      </span>
                      <span className="CourseAdmin-badge-price">
                        ₹{course.price}
                      </span>
                    </div>

                    <div className="CourseAdmin-card-body">
                      <div className="CourseAdmin-card-meta-top">
                        <span className="CourseAdmin-tag">
                          {course.code || course.category}
                        </span>
                        <span className="CourseAdmin-order-tag">
                          Order #{course.order}
                        </span>
                      </div>

                      <h3 className="CourseAdmin-card-title">
                        {course.title}
                      </h3>
                      <p className="CourseAdmin-card-desc">
                        {course.description}
                      </p>

                      <div className="CourseAdmin-meta-pills">
                        <span>⏳ {course.duration}</span>
                        <span>📊 {course.level}</span>
                        <span>💼 {course.projects} Projects</span>
                        <span>🎓 {course.internship}</span>
                      </div>

                      <div className="CourseAdmin-enrolled-count">
                        👥 Enrolled:{' '}
                        <strong>
                          {course.enrolledStudents?.length || 0}
                        </strong>
                      </div>

                      <div className="CourseAdmin-card-actions">
                        <button
                          type="button"
                          className="CourseAdmin-btn-action CourseAdmin-btn-students"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCourseStudents(course._id);
                          }}
                          disabled={studentsModalLoading}
                        >
                          👥 Roster
                        </button>

                        <div
                          className="CourseAdmin-kebab-wrapper"
                          ref={isMenuOpen ? menuRef : null}
                        >
                          <button
                            type="button"
                            className="CourseAdmin-kebab-btn"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setConfirmDeleteId(null);
                              setOpenMenuId((prev) =>
                                prev === course._id ? null : course._id
                              );
                            }}
                            aria-label="More actions"
                            aria-expanded={isMenuOpen}
                          >
                            ⋯
                          </button>

                          {isMenuOpen && (
                            <div
                              className="CourseAdmin-kebab-menu"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {confirmDeleteId === course._id ? (
                                <>
                                  <p className="CourseAdmin-confirm-text">
                                    Delete this course?
                                  </p>
                                  <button
                                    type="button"
                                    className="CourseAdmin-danger"
                                    onClick={() =>
                                      handleDeleteCourse(
                                        course._id,
                                        course.title
                                      )
                                    }
                                  >
                                    Yes, delete
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteId(null)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openEditModal(course)}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleCourseStatus(course)}
                                  >
                                    {course.isActive
                                      ? '🚫 Hide'
                                      : '👁️ Show'}
                                  </button>
                                  <button
                                    type="button"
                                    className="CourseAdmin-danger"
                                    onClick={() =>
                                      setConfirmDeleteId(course._id)
                                    }
                                  >
                                    🗑️ Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ENROLLMENTS TAB */}
      {currentSubTab === 'enrollments' && (
        <EnrollmentsView users={usersWithEnrollments} />
      )}

      {/* FORM MODAL — SEPARATE FILE */}
      <CourseFormModal
        isOpen={showFormModal}
        onClose={closeFormModal}
        onSubmit={handleFormSubmit}
        editingCourse={editingCourse}
        formData={formData}
        onChange={handleFormChange}
        onNestedChange={handleNestedChange}
        onFeatureChange={handleFeatureChange}
        onAddFeature={addFeature}
        onRemoveFeature={removeFeature}
        onAddSyllabusModule={addSyllabusModule}
        onRemoveSyllabusModule={removeSyllabusModule}
        submitting={submitting}
        getImageUrl={getImageUrl}
        onImageUploaded={(url) =>
          setFormData((prev) => ({ ...prev, imageUrl: url }))
        }
        uploadEndpoint={`${COURSE_API_URL}/admin/upload`}
        getAuthHeaders={getAuthHeaders}
      />

      {/* STUDENTS MODAL */}
      {selectedCourseStudents && (
        <CourseStudentsModal
          data={selectedCourseStudents}
          onClose={() => setSelectedCourseStudents(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   SMALL HELPERS
   ========================================================================== */

function KPI({ label, value, tone }) {
  return (
    <div className="CourseAdmin-kpi">
      <span>{label}</span>
      <strong className={tone ? `kpi-${tone}` : ''}>{value}</strong>
    </div>
  );
}